import {
  HStack,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import React from 'react';
import { Box, useToken } from '@chakra-ui/react';
import {
  ADJUSTABLE_NUTRITION_BLOCKS,
  BLOCK_TYPE,
  BlockArchetype,
  UserBlock,
} from '../../../models/block';
import {
  getAssignedBy,
  getBlockDateRangeLabel,
  getDaysAgoLabel,
  getDaysSince,
} from './utils';
import { LastEngagedRow } from './LastEngagedRow';
import { useDashboardPageState } from '../../../hooks/useDashboardPageState';
import { getIntercomConversationsUrl } from '../../../constants/misc';
import {
  AlertTriangle,
  Calendar,
  CircleCheckIcon,
  LucideIcon,
} from 'lucide-react';

const laneBlockDetailConfig: {
  label: string;
  field: keyof UserBlock;
  color?: string;
}[] = [{ label: 'Date archived', field: 'date_archived', color: 'red.500' }];

const BlockInfoItem = ({
  label,
  value,
  defaultValue,
  color = 'blueGray.600',
}: {
  label?: string;
  value?: string | number | null;
  defaultValue?: string;
  color?: string;
}) => {
  if (value || defaultValue)
    return (
      <Text color={color} fontSize={'12px'} mt={1}>
        {label ? `${label}: ` : ''}
        {value || defaultValue}
      </Text>
    );

  return null;
};

// Strip across the top of the card, e.g. Completed or Nudge.
const CardBanner = ({
  icon: Icon,
  label,
  color,
  bgColor,
  actionLabel,
  onAction,
  actionDisabled,
}: {
  icon: LucideIcon;
  label: string;
  color: string;
  bgColor?: string;
  actionLabel: string;
  onAction?: () => void;
  actionDisabled?: boolean;
}) => {
  const [iconColor] = useToken('colors', [color]);

  const action = (
    <Text fontSize={'13px'} fontWeight={600} color={color}>
      {actionLabel}
    </Text>
  );

  return (
    <HStack
      px={3}
      py={2}
      mb={0}
      alignItems={'center'}
      justifyContent={'space-between'}
      w={'100%'}
      gap={1}
      bgColor={bgColor}
      borderTopRadius={'xl'}
    >
      <HStack gap={1} alignItems={'center'}>
        <Icon color={iconColor} size={16} />
        <Text fontSize={'sm'} color={color} fontWeight={600}>
          {label}
        </Text>
      </HStack>
      {onAction ? (
        <Pressable onClick={onAction} disabled={actionDisabled}>
          {action}
        </Pressable>
      ) : (
        action
      )}
    </HStack>
  );
};

export const LaneBlockCard = ({
  block,
  openEditBlockDrawer,
  openNutritionBlockDetailsDrawer,
  handleArchive,
  enableCompletedBanner,
}: {
  block: UserBlock;
  openNutritionBlockDetailsDrawer: (block: UserBlock) => void;
  openEditBlockDrawer: (block: UserBlock) => void;
  handleArchive: () => void;
  enableCompletedBanner?: boolean;
}) => {
  const [blueGray600] = useToken('colors', ['blueGray.600']);
  const {
    state: { user },
  } = useDashboardPageState();
  const intercomId = user?.intercom_id;

  const canOpenDetailsDrawer =
    !!block.id &&
    !!block.stable_string &&
    !!ADJUSTABLE_NUTRITION_BLOCKS.includes(block.stable_string);

  const canOpenEditDrawer =
    block.archetype === BlockArchetype.Goal ||
    block.block_type === BLOCK_TYPE.MindGut ||
    block.block_type === BLOCK_TYPE.Nutrition;

  const isClickable = canOpenDetailsDrawer || canOpenEditDrawer;
  const showCompletedBanner = !!enableCompletedBanner && !!block.date_ended;
  const isContraindicated = block.archetype === BlockArchetype.Contraindicated;
  const dateRangeLabel = getBlockDateRangeLabel(
    block.date_assigned,
    block.date_ended
  );
  const lastEngaged = block.metadata?.last_engaged;
  const daysSinceEngaged = lastEngaged ? getDaysSince(lastEngaged) : null;
  const showNudgeBanner =
    !!block.metadata?.should_nudge &&
    !block.date_ended &&
    !block.date_archived;

  return (
    <VStack
      gap={0}
      w={'100%'}
      bgColor={'mint'}
      borderRadius={'xl'}
      borderWidth={1}
      mb={0}
    >
      {showCompletedBanner && (
        <CardBanner
          icon={CircleCheckIcon}
          label={'Completed'}
          color={'seafoam'}
          actionLabel={'Archive'}
          onAction={handleArchive}
          actionDisabled={!!block.date_archived}
        />
      )}
      {showNudgeBanner && (
        <CardBanner
          icon={AlertTriangle}
          label={
            daysSinceEngaged !== null
              ? getDaysAgoLabel(daysSinceEngaged)
              : 'Needs a nudge'
          }
          color={'secondary.500'}
          bgColor={'gold'}
          actionLabel={'Nudge'}
          actionDisabled={!intercomId}
          onAction={() => {
            if (!intercomId) return;

            window.open(
              getIntercomConversationsUrl(intercomId),
              '_blank',
              'noopener,noreferrer'
            );
          }}
        />
      )}
      <Pressable
        w="100%"
        disabled={!isClickable}
        onClick={() => {
          if (canOpenDetailsDrawer) {
            openNutritionBlockDetailsDrawer(block);
            return;
          }
          if (canOpenEditDrawer) {
            openEditBlockDrawer(block);
          }
        }}
      >
        <Box
          borderColor={'cloud.200'}
          borderRadius={'xl'}
          borderWidth={1}
          m={'-1px'}
          bgColor={isContraindicated ? 'ballet' : 'white'}
        >
          <Box p={3}>
            <Text
              color={isContraindicated ? 'plum' : 'secondary.500'}
              fontSize={'16px'}
              fontWeight={'normal'}
              textAlign={'left'}
            >
              {block.block_name}
            </Text>

            <BlockInfoItem value={getAssignedBy(block?.assigned_by)} />

            {dateRangeLabel && (
              <HStack alignItems={'center'} gap={2} mt={'12px'}>
                <Calendar color={blueGray600} size={14} strokeWidth={2} />
                <Text color={'blueGray.600'} fontSize={'12px'}>
                  {dateRangeLabel}
                </Text>
              </HStack>
            )}

            {laneBlockDetailConfig
              .filter(({ field }) => !!block[field])
              .map(({ label, field, color }) => (
                <BlockInfoItem
                  key={label}
                  color={color}
                  label={label}
                  value={block[field] as string | number}
                />
              ))}

            <BlockInfoItem
              label="Logged gut checks"
              value={block.metadata?.logged_gut_checks}
            />
          </Box>

          {daysSinceEngaged !== null && (
            <Box borderTopWidth={1} borderTopColor={'cloud.200'} px={3} py={2}>
              <LastEngagedRow days={daysSinceEngaged} />
            </Box>
          )}
        </Box>
      </Pressable>
    </VStack>
  );
};

export default LaneBlockCard;
